import { Link } from "react-router-dom";
import { Check, Download } from "lucide-react";
import { Reveal, LineReveal } from "@/components/site/motion";
import { Exhibit } from "@/components/site/Exhibit";
import { SCREENS, roman } from "@/data/screens";

const FeatureRow = ({ s, i }) => {
  const flip = i % 2 === 1;
  return (
    <section className="border-t border-slate-200 py-16 sm:py-24" data-testid={`feature-section-${s.id}`}>
      <div className="container-x grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
        <Reveal className={`lg:col-span-5 ${flip ? "lg:order-2 lg:col-start-8" : ""}`}>
          <p className="eyebrow">{String(i + 1).padStart(2, "0")} &middot; {s.label}</p>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-navy sm:text-4xl">{s.title}</h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600">{s.body}</p>
          <ul className="mt-7 space-y-3">
            {s.points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#8a6a2f]" />
                {p}
              </li>
            ))}
          </ul>
          {s.download && (
            <a href={s.download.href} download className="btn-outline mt-8" data-testid={`feature-download-${s.id}`}>
              <Download className="h-4 w-4" /> {s.download.label}
            </a>
          )}
        </Reveal>
        <Reveal delay={0.08} className={`lg:col-span-7 ${flip ? "lg:order-1 lg:col-start-1" : ""}`}>
          <div className="exhibit-frame">
            <Exhibit shot={s} plate={roman(i + 1)} />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Features() {
  return (
    <div data-testid="features-page">
      <header className="bg-parchment">
        <div className="container-x py-16 sm:py-24">
          <p className="eyebrow">Features</p>
          <LineReveal lines={["Thirteen plates,", "one orderly ledger."]} className="mt-6 max-w-3xl font-display text-5xl font-semibold leading-[1.04] tracking-tight text-navy sm:text-6xl" />
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">A tour of Grace Treasury, screen by screen. Click any plate to see the full screenshot; on longer pages, hover to scroll through.</p>
            <div className="mt-10 max-w-2xl border-l-2 border-gold bg-white py-5 pl-5 pr-6" data-testid="features-expectations-note">
              <p className="eyebrow">What to expect</p>
              <p className="mt-3 text-base leading-relaxed text-slate-700">
                Grace Treasury is a simple accounting and reporting tool. It does not connect to your bank. Your team records transactions or imports them from a CSV file, then reconciles each account against the bank statement. Nothing syncs automatically, so every entry in your books is one someone has recorded and reviewed.
              </p>
            </div>
          </Reveal>
        </div>
      </header>
      {SCREENS.map((s, i) => <FeatureRow key={s.id} s={s} i={i} />)}
      <section className="bg-navy py-16 text-white sm:py-24">
        <Reveal className="container-x flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <h2 className="max-w-2xl font-display text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">Would you like to see it with your own questions?</h2>
          <Link to="/demo" className="btn-gold" data-testid="features-request-demo-button">Request a demo</Link>
        </Reveal>
      </section>
    </div>
  );
}
